import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Divider, Flex, Skeleton, Stack, Text, useDisclosure} from '@chakra-ui/react'
import ProductSetCockpitTotal from './product-set-cockpit-total'
import {FormattedMessage} from 'react-intl'
import ProductSetModal from './product-set-modal'
import ProductSetTile from '../../../components/product-set-tile'
import {trackPsdpClickProductTile} from '../../../tracking/analytics'
import ProductSpecialOffers from '../../../components/special-offer/product-special-offers'
import CockpitOrderSection from './cockpit-order-section'
import useMultiSite from '../../../hooks/use-multi-site'
import {useProduct} from '../../../hooks'
import AddToCartCTA, {Skeleton as AddToCartCTASkeleton} from '../../../components/add-to-cart-cta'
import WhereToBuyCTA from '../../../components/where-to-buy-cta'
import HybridLink from '../../../components/hybrid-link'
import {HYBRID_STORE_LOCATOR, STORE_LOCATOR_HREF} from '../../../constants'

const PsdpCockpitRightColumn = ({
    configureRef,
    isPromotionsLoading,
    isVariantLoading,
    product,
    promotions,
    rightSidebarSizeProps,
    stickyProps,
    onAddToCartClick,
    onClickEngraving
}) => {
    const {site} = useMultiSite()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [modalSliderIndex, setModalSliderIndex] = useState(0)
    const {isEngravable} = useProduct(product)

    return (
        <Stack
            data-testid={'product-set-cockpit-right-column'}
            spacing={4}
            px={{base: 4, lg: 0}}
            scrollMarginTop={24}
            {...stickyProps}
            {...rightSidebarSizeProps}
            ref={configureRef}
        >
            {/* Frame 3319 */}
            <Stack spacing={4}>
                {/*Set Total*/}
                <ProductSetCockpitTotal product={product} display={{base: 'none', lg: 'flex'}} />

                <Divider display={{base: 'none', lg: 'block'}} borderColor="stokkeGray.dark" />

                <Stack spacing={2}>
                    <Text as={'h2'} textStyle="Regular XLarge">
                        <FormattedMessage
                            id="product_set_detail.cockpit.title"
                            defaultMessage="Make your set unique!"
                        />
                    </Text>
                    <Text textStyle="Regular Small">
                        <FormattedMessage
                            id="product_set_detail.cockpit.description"
                            defaultMessage="Choose different combinations by clicking on the tile."
                        />
                    </Text>
                </Stack>

                <ProductSetModal
                    initialIndex={modalSliderIndex}
                    isOpen={isOpen}
                    products={product.setProducts}
                    onAddToCartClick={onAddToCartClick}
                    onClose={onClose}
                />
                {product.setProducts.map((childProduct, index) => (
                    <Box key={childProduct.id + '_' + index} data-testid="child-product">
                        <ProductSetTile
                            onOpen={() => {
                                setModalSliderIndex(index)
                                onOpen()
                                trackPsdpClickProductTile(childProduct)
                            }}
                            productSetIndex={index}
                            product={childProduct}
                            dynamicImageProps={{
                                widths: ['70vw', '70vw', '40vw', '30vw']
                            }}
                        />
                    </Box>
                ))}
            </Stack>

            <Stack spacing={{base: 4, lg: 6}} pt={{lg: 2}}>
                {!site?.isContentOnlySite && isEngravable && (
                    <Button variant="outline" size="lg" onClick={onClickEngraving}>
                        <FormattedMessage
                            id="product_set_detail.cockpit.add_all_to_basket_incl_engraving"
                            defaultMessage="Add all to basket incl. engraving"
                        />
                    </Button>
                )}

                <ProductSetCockpitTotal product={product} />
            </Stack>

            {/* Special offer on mobile */}
            <Skeleton isLoaded={!isPromotionsLoading && !isVariantLoading}>
                <Box display={{base: 'block', lg: 'none'}}>
                    <ProductSpecialOffers product={product} promotions={promotions} />
                </Box>
            </Skeleton>

            {/* Bottom */}
            <Flex flexDirection="column" gap={4}>
                <Flex flexDirection="column" gap={3}>
                    {!isVariantLoading ? (
                        <AddToCartCTA
                            isPromotionsLoading={isPromotionsLoading}
                            isVariantLoading={isVariantLoading}
                            product={product}
                            promotions={promotions}
                            onClick={onAddToCartClick}
                        />
                    ) : (
                        <AddToCartCTASkeleton />
                    )}
                    {!site?.isContentOnlySite && <WhereToBuyCTA product={product} />}
                    <Button
                        variant="outline"
                        size="lg"
                        width="100%"
                        as={HybridLink}
                        page={HYBRID_STORE_LOCATOR}
                        to={STORE_LOCATOR_HREF}
                        isExternal
                    >
                        <FormattedMessage
                            id="product_view.button.experience_in_store"
                            defaultMessage="Experience in store"
                        />
                    </Button>
                </Flex>

                <CockpitOrderSection
                    product={product}
                    promotions={promotions}
                    isPromotionsLoading={isPromotionsLoading}
                    isProductASet
                    isVariantLoading={isVariantLoading}
                    onAddToCartClick={onAddToCartClick}
                />
            </Flex>
        </Stack>
    )
}

PsdpCockpitRightColumn.propTypes = {
    configureRef: PropTypes.object,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    product: PropTypes.object,
    promotions: PropTypes.array,
    rightSidebarSizeProps: PropTypes.object,
    setProductProps: PropTypes.object,
    stickyProps: PropTypes.object,
    onAddToCartClick: PropTypes.func,
    onClickEngraving: PropTypes.func
}

export default PsdpCockpitRightColumn
